import {
  useGetSubscription,
  useListPaymentMethods,
  useListStripeInvoices,
  useUpdateSubscription,
} from "@/api/stripe";
import { useUserInfo } from "@/api/user";
import {
  chipButtonClassName,
  EmptyState,
  NESTED_DIALOG_Z,
  NeutralBadge,
  PaneBody,
  paneFieldInputClassName,
  PaneSection,
  SettingRow,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { SubscriptionSection } from "@/components/billing/SubscriptionSection";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ROUTES } from "@/constants/routes";
import { useBetaPlan } from "@/hooks/useBetaPlan";
import { useStartPaymentMethodSetup } from "@/hooks/useStartPaymentMethodSetup";
import { cn } from "@/utils";
import {
  trackBillingBudgetUpdated,
  trackBillingEmailUpdated,
  trackBillingInvoiceDownloaded,
  trackBillingInvoiceViewed,
  trackBillingPaymentMethodUpdateStarted,
} from "@/utils/analytics";
import { formatCurrencyFromCents, formatInteger } from "@/utils/currency";
import { describePaymentMethod } from "@/utils/paymentMethod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Download } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

/**
 * Settings · Plan & billing · Billing — how you pay: the card on file, where
 * receipts go, what has been billed, and the two verbs that change the
 * subscription's fate. What you have and what you've used is one scope over
 * in Plan.
 *
 * Cancel and reactivate swap in place, each behind a confirm: work is never
 * on the table, billing is. Card entry opens over this pane.
 */

const billingEmailSchema = z.object({
  billing_email: z.string().email("Please enter a valid email address"),
});
type BillingEmailFormData = z.infer<typeof billingEmailSchema>;

export const BillingPane: React.FC = () => {
  const { data: userInfo } = useUserInfo();
  const { data: subscription, isLoading: isSubscriptionLoading } =
    useGetSubscription();
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } =
    useListPaymentMethods();
  const { data: invoices, isLoading: isInvoicesLoading } =
    useListStripeInvoices();
  const updateSubscription = useUpdateSubscription();
  const isBetaPlan = useBetaPlan();
  const startPaymentMethodSetup = useStartPaymentMethodSetup();

  const [showBillingEmailDialog, setShowBillingEmailDialog] = useState(false);
  const [budgetAmount, setBudgetAmount] = useState("");
  const [isUnlimitedBudget, setIsUnlimitedBudget] = useState(false);
  const [isBudgetSaving, setIsBudgetSaving] = useState(false);

  const plan = subscription?.data.stripe_subscription;
  const planName = plan?.plan_name;
  const billingInterval = plan?.billing_interval;
  const seatCount = plan?.seat_count || 1;
  const seatPrice = plan?.seat_price || 0;
  const creditPrice = plan?.credit_price || 0;
  const maxOverageSpend = plan?.max_credit_overage_spend_per_period || 0;
  const isOnFreeTrial = plan?.status === "trialing";

  const formattedBillingDate = plan?.current_period_end
    ? new Date(plan.current_period_end).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  // The plan row moved to the Plan scope; what Billing owes the reader is the
  // next number to leave their account.
  const nextCharge =
    seatPrice && formattedBillingDate
      ? `${isOnFreeTrial ? "First" : "Next"} charge ${formatCurrencyFromCents(
          seatPrice * seatCount,
        )} on ${formattedBillingDate}`
      : null;

  const billingEmailForm = useForm<BillingEmailFormData>({
    resolver: zodResolver(billingEmailSchema),
    defaultValues: { billing_email: plan?.billing_email || "" },
  });

  useEffect(() => {
    if (plan?.billing_email) {
      billingEmailForm.reset({ billing_email: plan.billing_email });
    }
  }, [plan?.billing_email, billingEmailForm]);

  useEffect(() => {
    if (!plan) return;
    if (maxOverageSpend === -1) {
      setIsUnlimitedBudget(true);
      setBudgetAmount("");
      return;
    }
    setIsUnlimitedBudget(false);
    setBudgetAmount(maxOverageSpend >= 0 ? String(maxOverageSpend / 100) : "");
  }, [plan, maxOverageSpend]);

  const defaultPaymentMethod = paymentMethods?.data.payment_methods?.find(
    (method) => method.default,
  );
  const budgetAmountNumber =
    budgetAmount === "" ? 0 : parseFloat(budgetAmount) || 0;
  const budgetCredits =
    creditPrice > 0 ? Math.floor((budgetAmountNumber * 100) / creditPrice) : 0;
  const isBudgetDirty =
    !isUnlimitedBudget &&
    Math.round(budgetAmountNumber * 100) !== Math.max(maxOverageSpend, 0);

  /**
   * Every subscription write resends the plan triple — the endpoint takes a
   * whole subscription, not a patch.
   */
  const updateSubscriptionField = async (
    field: Partial<{
      billing_email: string;
      max_credit_overage_spend_per_period: number;
    }>,
  ) => {
    if (!planName || !billingInterval) {
      throw new Error("Missing subscription information.");
    }
    await updateSubscription.mutateAsync({
      plan_name: planName,
      billing_interval: billingInterval,
      seat_count: seatCount,
      ...field,
    });
  };

  /**
   * `unlimited` is passed explicitly by the switch: calling this from inside
   * its own change handler would otherwise read the pre-toggle state.
   */
  const saveBudget = async (unlimited: boolean = isUnlimitedBudget) => {
    try {
      setIsBudgetSaving(true);
      await updateSubscriptionField({
        max_credit_overage_spend_per_period: unlimited
          ? -1
          : Math.round(budgetAmountNumber * 100),
      });
      trackBillingBudgetUpdated({
        budgetAmount: unlimited ? undefined : budgetAmountNumber,
        isUnlimited: unlimited,
        planName,
        organization_id: userInfo?.organization?.id,
      });
      toast.success("Budget updated.");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update the budget.",
      );
    } finally {
      setIsBudgetSaving(false);
    }
  };

  const saveBillingEmail = async (data: BillingEmailFormData) => {
    try {
      await updateSubscriptionField({ billing_email: data.billing_email });
      trackBillingEmailUpdated({
        planName,
        organization_id: userInfo?.organization?.id,
      });
      toast.success("Billing email updated.");
      setShowBillingEmailDialog(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update the billing email.",
      );
    }
  };

  if (!plan && !isSubscriptionLoading) {
    return (
      <PaneBody>
        <EmptyState
          title="No subscription yet."
          description="Billing details appear here once a plan is active."
        />
      </PaneBody>
    );
  }

  return (
    <PaneBody>
      <PaneSection head="Billing" desc={nextCharge}>
        <div data-testid="payment-method-section">
          <SettingRow
            label="Payment method"
            desc={
              isPaymentMethodsLoading ? (
                <span className="inline-block h-4 w-40 animate-pulse rounded bg-n-10 dark:bg-n-20" />
              ) : (
                (describePaymentMethod(defaultPaymentMethod) ??
                "No payment method on file")
              )
            }
          >
            <Button
              data-testid="add-payment-method-button"
              variant="ghost"
              size="sm"
              className={chipButtonClassName}
              disabled={isPaymentMethodsLoading}
              onClick={() => {
                trackBillingPaymentMethodUpdateStarted({
                  source: "usage_billing",
                  currentPlan: planName,
                  organization_id: userInfo?.organization?.id,
                });
                // keepTrial preserves remaining trial days when a card is added
                // mid-trial (otherwise the confirm page ends the trial and
                // charges immediately).
                startPaymentMethodSetup({
                  redirectTo: ROUTES.SETTINGS.USAGE_BILLING,
                  keepTrial: true,
                });
              }}
            >
              {defaultPaymentMethod ? "Update" : "Add"}
            </Button>
          </SettingRow>
        </div>

        <SettingRow
          label="Billing email"
          desc={plan?.billing_email || "No billing email set"}
        >
          <Button
            variant="ghost"
            size="sm"
            className={chipButtonClassName}
            disabled={isSubscriptionLoading}
            onClick={() => setShowBillingEmailDialog(true)}
          >
            {plan?.billing_email ? "Update" : "Set"}
          </Button>
        </SettingRow>
      </PaneSection>

      {/* Beta orgs have a server-provisioned overage limit and the backend
          rejects updates with 403 — hide the control rather than fail it. */}
      {plan && !isOnFreeTrial && !isBetaPlan && (
        <PaneSection head="On-demand budget">
          <SettingRow
            label="Unlimited"
            desc="Allow on-demand credit spending without a cap"
          >
            <Switch
              checked={isUnlimitedBudget}
              onCheckedChange={(checked) => {
                setIsUnlimitedBudget(checked);
                // Applies on toggle — there is no Save beside it any more.
                void saveBudget(checked);
              }}
              disabled={isBudgetSaving || updateSubscription.isPending}
              aria-label="Unlimited on-demand budget"
            />
          </SettingRow>
          {/* The amount is a row like every other one in this pane, so its
              label, field and Save land in the same columns. Save belongs to
              the amount, so it only exists while there is an amount to save —
              the switch itself applies on toggle, like every other control in
              the modal. */}
          {!isUnlimitedBudget && (
            <SettingRow
              label="Budget per period"
              desc={
                budgetAmount === "" || budgetAmountNumber === 0
                  ? "No on-demand spending"
                  : `≈ ${formatInteger(budgetCredits)} credits at $${(
                      creditPrice / 100
                    ).toFixed(3)} each`
              }
            >
              <div className="relative w-32">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-n-50">
                  $
                </span>
                <Input
                  id="budget-amount"
                  aria-label="Budget per period"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className={cn(paneFieldInputClassName, "pl-7")}
                  value={budgetAmount}
                  onChange={(event) => setBudgetAmount(event.target.value)}
                />
              </div>
              {isBudgetDirty && (
                <Button
                  size="sm"
                  variant="secondary"
                  className={smButtonClassName}
                  onClick={() => void saveBudget()}
                  disabled={isBudgetSaving || updateSubscription.isPending}
                  isLoading={isBudgetSaving}
                >
                  Save
                </Button>
              )}
            </SettingRow>
          )}
        </PaneSection>
      )}

      <PaneSection head="Invoices">
        {isInvoicesLoading ? (
          <div className="space-y-3 pt-1">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="h-5 w-full animate-pulse rounded bg-n-10 dark:bg-n-20"
              />
            ))}
          </div>
        ) : invoices?.data.stripe_invoices?.length ? (
          <div className="flex flex-col gap-0.5">
            {invoices.data.stripe_invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="flex min-h-10 items-center gap-3 px-2.5 py-1.5"
              >
                <span className="w-24 shrink-0 text-base text-n-90">
                  {new Date(invoice.created_at).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
                <span className="flex-1 truncate font-mono text-xs text-n-40">
                  {invoice.number}
                </span>
                <span className="shrink-0 text-sm text-n-70">
                  {formatCurrencyFromCents(invoice.total)}
                </span>
                <NeutralBadge>
                  {invoice.status.charAt(0).toUpperCase() +
                    invoice.status.slice(1)}
                </NeutralBadge>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0 text-n-50 hover:text-n-90"
                  aria-label={`Download invoice ${invoice.number}`}
                  onClick={() => {
                    trackBillingInvoiceViewed({
                      invoiceId: invoice.id,
                      invoiceAmount: invoice.total,
                      invoiceStatus: invoice.status,
                      organization_id: userInfo?.organization?.id,
                    });
                    trackBillingInvoiceDownloaded({
                      invoiceId: invoice.id,
                      invoiceAmount: invoice.total,
                      invoiceStatus: invoice.status,
                      organization_id: userInfo?.organization?.id,
                    });
                    window.open(invoice.download_url, "_blank");
                  }}
                >
                  <Download />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No invoices yet."
            description="Receipts appear here once the first charge lands."
          />
        )}
      </PaneSection>

      <SubscriptionSection />

      <Dialog
        open={showBillingEmailDialog}
        onOpenChange={setShowBillingEmailDialog}
      >
        <DialogContent
          className={NESTED_DIALOG_Z}
          overlayClassName={NESTED_DIALOG_Z}
        >
          <DialogHeader>
            <DialogTitle>Billing email</DialogTitle>
            <DialogDescription>
              Where invoices and receipts are sent.
            </DialogDescription>
          </DialogHeader>
          <Form {...billingEmailForm}>
            <form
              onSubmit={billingEmailForm.handleSubmit(saveBillingEmail)}
              className="space-y-4"
            >
              <FormField
                control={billingEmailForm.control}
                name="billing_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="billing@example.com"
                        type="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowBillingEmailDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateSubscription.isPending}
                  isLoading={updateSubscription.isPending}
                >
                  Save
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </PaneBody>
  );
};
